import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMAuthorityAdminList from "../../features/university-manager/UMAuthorityAdminList";
import UMAuthorityAddAdminModal from "../../features/university-manager/UMAuthorityAddAdminModal";
import UMAuthorityProfilePicker from "../../features/university-manager/UMAuthorityProfilePicker";
import UMAuthorityPermissionMatrix from "../../features/university-manager/UMAuthorityPermissionMatrix";
import UMAuthorityClubAccess from "../../features/university-manager/UMAuthorityClubAccess";
import AppButton from "../../components/ui/AppButton";
import umService from "../../services/university-admin/umService";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { useNotification } from "../../context/NotificationContext";
import { groupCatalogByPrimaryModule } from "../../utils/authorityLabels";
import { PROFILE_PRESETS, detectActivePreset, diffPresetApplication } from "../../utils/authorityPresets";

const PAGE_SIZE = 50;

const EMPTY_ADD_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  phoneNumber: "",
};

const UMAuthorityManagement = () => {
  const { t } = useTranslation(["staff", "common"]);
  const { isSuperAdmin, permissionsLoading } = useUniAdminAuth();
  const { showSuccess, showError } = useNotification();

  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [typeChangeLoadingId, setTypeChangeLoadingId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState("promote");
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addLoading, setAddLoading] = useState(false);

  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [togglingGrantKey, setTogglingGrantKey] = useState(null);
  const [applyingPresetId, setApplyingPresetId] = useState(null);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [resettingPermissions, setResettingPermissions] = useState(false);

  const [clubs, setClubs] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [togglingClubId, setTogglingClubId] = useState(null);
  const [clubSearchTerm, setClubSearchTerm] = useState("");
  const [togglingAllClubsAccess, setTogglingAllClubsAccess] = useState(false);

  const isSuper = isSuperAdmin;

  const loadAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    const res = await umService.getStaffAdmins(currentPage, PAGE_SIZE);
    if (res.success) {
      setAdmins(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } else {
      showError(t("staff:messages.loadFailed"));
    }
    setLoadingAdmins(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, refreshTrigger]);

  useEffect(() => {
    if (isSuper) loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, loadAdmins]);

  useEffect(() => {
    if (!isSuper) return;
    umService.getAllClubs({}).then((res) => {
      if (res.success) setClubs(res.data);
    });
    setCatalogLoading(true);
    umService.getPermissionsCatalog().then((res) => {
      if (res.success) setCatalog(res.data);
      setCatalogLoading(false);
    });
  }, [isSuper]);

  const loadAdminDetail = useCallback(
    async (admin) => {
      setGrantsLoading(true);
      setScopesLoading(true);
      const [grantsRes, scopesRes] = await Promise.all([
        umService.getAdminGrants(admin.id),
        umService.getAdminClubScopes(admin.id),
      ]);
      if (grantsRes.success) {
        setGrants(grantsRes.data);
      } else {
        setGrants([]);
        showError(t("staff:messages.loadDetailFailed"));
      }
      setScopes(scopesRes.success ? scopesRes.data : []);
      setGrantsLoading(false);
      setScopesLoading(false);
    },
    [t, showError]
  );

  const handleSelectAdmin = (admin) => {
    setSelectedAdmin(admin);
    setClubSearchTerm("");
    loadAdminDetail(admin);
  };

  const superAdminCount = useMemo(
    () => admins.filter((a) => a.adminType === "SUPER_ADMIN").length,
    [admins]
  );

  const handleChangeType = async (admin) => {
    const nextType = admin.adminType === "SUPER_ADMIN" ? "SUB_ADMIN" : "SUPER_ADMIN";
    setTypeChangeLoadingId(admin.id);
    const res = await umService.changeAdminType(admin.id, nextType);
    setTypeChangeLoadingId(null);
    if (!res.success) {
      showError(t("staff:messages.typeChangeFailed"));
      return;
    }
    showSuccess(t("staff:messages.typeChangeSuccess"));
    setRefreshTrigger((x) => x + 1);
    setSelectedAdmin((prev) => (prev?.id === admin.id ? { ...prev, adminType: nextType } : prev));
  };

  const openAddModal = (mode) => {
    setAddMode(mode);
    setAddForm(EMPTY_ADD_FORM);
    setShowAddModal(true);
  };

  const handleAddFieldChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    const res =
      addMode === "promote"
        ? await umService.promoteAdmin(addForm.email.trim())
        : await umService.createStaffAdmin({
            firstName: addForm.firstName.trim(),
            lastName: addForm.lastName.trim(),
            email: addForm.email.trim(),
            password: addForm.password,
            phoneNumber: addForm.phoneNumber.trim(),
          });
    setAddLoading(false);

    if (!res.success) {
      showError(t(addMode === "promote" ? "staff:messages.promoteFailed" : "staff:messages.createFailed"));
      return;
    }

    showSuccess(t(addMode === "promote" ? "staff:messages.promoteSuccess" : "staff:messages.createSuccess"));
    setShowAddModal(false);
    setRefreshTrigger((x) => x + 1);
  };

  const groupedCatalog = useMemo(() => groupCatalogByPrimaryModule(catalog), [catalog]);

  const isGranted = useCallback(
    (entry) => grants.some((g) => g.httpMethod === entry.httpMethod && g.pathTemplate === entry.pathTemplate),
    [grants]
  );

  const activePresetId = useMemo(
    () => detectActivePreset(PROFILE_PRESETS, catalog, grants),
    [catalog, grants]
  );

  const handleToggleGrant = async (entry) => {
    if (!selectedAdmin) return;
    const key = `${entry.httpMethod} ${entry.pathTemplate}`;
    const granted = isGranted(entry);
    setTogglingGrantKey(key);

    setGrants((prev) =>
      granted
        ? prev.filter((g) => !(g.httpMethod === entry.httpMethod && g.pathTemplate === entry.pathTemplate))
        : [...prev, { httpMethod: entry.httpMethod, pathTemplate: entry.pathTemplate, adminUserId: selectedAdmin.id }]
    );

    const res = granted
      ? await umService.revokePermission({
          adminUserId: selectedAdmin.id,
          httpMethod: entry.httpMethod,
          pathTemplate: entry.pathTemplate,
        })
      : await umService.grantPermission({
          adminUserId: selectedAdmin.id,
          httpMethod: entry.httpMethod,
          pathTemplate: entry.pathTemplate,
        });

    setTogglingGrantKey(null);
    if (!res.success) loadAdminDetail(selectedAdmin);
  };

  const handleApplyPreset = async (preset) => {
    if (!selectedAdmin) return;
    const { toGrant, toRevoke } = diffPresetApplication(preset, catalog, grants);
    if (toGrant.length === 0 && toRevoke.length === 0) return;

    setApplyingPresetId(preset.id);

    const revokedKeys = new Set(toRevoke.map((g) => `${g.httpMethod} ${g.pathTemplate}`));
    setGrants((prev) => [
      ...prev.filter((g) => !revokedKeys.has(`${g.httpMethod} ${g.pathTemplate}`)),
      ...toGrant.map((e) => ({ httpMethod: e.httpMethod, pathTemplate: e.pathTemplate, adminUserId: selectedAdmin.id })),
    ]);

    const results = await Promise.all([
      ...toGrant.map((e) =>
        umService.grantPermission({ adminUserId: selectedAdmin.id, httpMethod: e.httpMethod, pathTemplate: e.pathTemplate })
      ),
      ...toRevoke.map((e) =>
        umService.revokePermission({ adminUserId: selectedAdmin.id, httpMethod: e.httpMethod, pathTemplate: e.pathTemplate })
      ),
    ]);

    setApplyingPresetId(null);

    if (results.some((r) => !r.success)) {
      showError(t("staff:messages.presetApplyPartialFailure"));
      loadAdminDetail(selectedAdmin);
    } else {
      showSuccess(t("staff:messages.presetApplySuccess"));
    }
  };

  const handleConfirmResetAllPermissions = async () => {
    if (!selectedAdmin || grants.length === 0) return;
    setResettingPermissions(true);

    const prevGrants = [...grants];
    setGrants([]);
    setShowResetConfirmModal(false);

    const results = await Promise.all(
      prevGrants.map((g) =>
        umService.revokePermission({
          adminUserId: selectedAdmin.id,
          httpMethod: g.httpMethod,
          pathTemplate: g.pathTemplate,
        })
      )
    );

    setResettingPermissions(false);

    if (results.some((r) => !r.success)) {
      showError(t("staff:messages.resetPermissionsPartialFailure"));
      loadAdminDetail(selectedAdmin);
    } else {
      showSuccess(t("staff:messages.resetPermissionsSuccess"));
    }
  };

  const isScoped = useCallback(
    (clubId) => scopes.some((s) => String(s.clubId) === String(clubId)),
    [scopes]
  );

  const handleToggleScope = async (club) => {
    if (!selectedAdmin) return;
    const scoped = isScoped(club.id);
    setTogglingClubId(club.id);

    setScopes((prev) =>
      scoped
        ? prev.filter((s) => String(s.clubId) !== String(club.id))
        : [...prev, { clubId: club.id, adminUserId: selectedAdmin.id }]
    );

    const res = scoped
      ? await umService.revokeClubScope({ adminUserId: selectedAdmin.id, clubId: club.id })
      : await umService.grantClubScope({ adminUserId: selectedAdmin.id, clubId: club.id });

    setTogglingClubId(null);
    if (!res.success) loadAdminDetail(selectedAdmin);
  };

  const handleToggleAllClubsAccess = async (enabled) => {
    if (!selectedAdmin) return;
    setTogglingAllClubsAccess(true);
    const res = await umService.setAllClubsAccess(selectedAdmin.id, enabled);
    setTogglingAllClubsAccess(false);

    if (!res.success) {
      showError(t("staff:messages.allClubsAccessFailed"));
      return;
    }

    showSuccess(t("staff:messages.allClubsAccessSuccess"));
    setSelectedAdmin((prev) => (prev?.id === selectedAdmin.id ? { ...prev, allClubsAccess: enabled } : prev));
    setAdmins((prev) => prev.map((a) => (a.id === selectedAdmin.id ? { ...a, allClubsAccess: enabled } : a)));
  };

  if (!isSuper) {
    return (
      <UniversityManagerLayout>
        {permissionsLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("common:actions.loading")}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-lock text-muted mb-3" style={{ fontSize: "3rem" }}></i>
            <h4 className="text-muted">{t("staff:accessDenied.title")}</h4>
            <p className="text-muted">{t("staff:accessDenied.body")}</p>
          </div>
        )}
      </UniversityManagerLayout>
    );
  }

  const isSelectedSuperAdmin = selectedAdmin?.adminType === "SUPER_ADMIN";

  return (
    <UniversityManagerLayout>
      <div className="authority-page">
        <div className="mb-4">
          <h1 className="h3 mb-1 fw-bold text-dark">{t("staff:title")}</h1>
          <p className="text-muted mb-0">{t("staff:subtitle")}</p>
        </div>

        <div className="row g-3 authority-layout">
          <div className="col-lg-5">
            <UMAuthorityAdminList
              admins={admins}
              loading={loadingAdmins}
              totalElements={totalElements}
              currentPage={currentPage}
              totalPages={totalPages}
              selectedAdminId={selectedAdmin?.id}
              superAdminCount={superAdminCount}
              typeChangeLoadingId={typeChangeLoadingId}
              onSelectAdmin={handleSelectAdmin}
              onAddAdmin={() => openAddModal("promote")}
              onChangeType={handleChangeType}
              onPrevPage={() => setCurrentPage((p) => p - 1)}
              onNextPage={() => setCurrentPage((p) => p + 1)}
            />
          </div>

          <div className="col-lg-7">
            {!selectedAdmin ? (
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center justify-content-center text-muted py-5">
                  {t("staff:detail.selectPrompt")}
                </div>
              </div>
            ) : (
              <>
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="user-avatar rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "48px", height: "48px" }}
                    >
                      <i className="fas fa-user-shield text-primary"></i>
                    </div>
                    <div>
                      <div className="fw-bold text-dark">{selectedAdmin.fullName}</div>
                      <small className="text-muted">{selectedAdmin.email}</small>
                    </div>
                    {isSelectedSuperAdmin ? (
                      <span className="badge bg-danger ms-auto">
                        <i className="fas fa-crown me-1"></i>
                        {t("staff:admins.type.SUPER_ADMIN")}
                      </span>
                    ) : (
                      selectedAdmin.allClubsAccess && (
                        <span className="badge bg-primary ms-auto">
                          <i className="fas fa-globe me-1"></i>
                          {t("staff:clubAccess.allClubsAccess.badgeShort")}
                        </span>
                      )
                    )}
                  </div>
                </div>

                {isSelectedSuperAdmin ? (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                      <i className="fas fa-shield-halved text-primary mb-3" style={{ fontSize: "2.5rem" }}></i>
                      <p className="text-muted mb-0">{t("staff:detail.superAdminNotice.body")}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <UMAuthorityProfilePicker
                      presets={PROFILE_PRESETS}
                      catalog={catalog}
                      grants={grants}
                      activePresetId={activePresetId}
                      applyingPresetId={applyingPresetId}
                      disabled={catalogLoading || grantsLoading}
                      onApplyPreset={handleApplyPreset}
                    />

                    <UMAuthorityPermissionMatrix
                      groupedCatalog={groupedCatalog}
                      isGranted={isGranted}
                      loading={catalogLoading || grantsLoading}
                      togglingKey={togglingGrantKey}
                      onToggle={handleToggleGrant}
                      grantsCount={grants.length}
                      resetting={resettingPermissions}
                      onResetAll={() => setShowResetConfirmModal(true)}
                    />

                    <UMAuthorityClubAccess
                      clubs={clubs}
                      isScoped={isScoped}
                      loading={scopesLoading}
                      togglingClubId={togglingClubId}
                      searchTerm={clubSearchTerm}
                      onSearchChange={setClubSearchTerm}
                      onToggleClub={handleToggleScope}
                      allClubsAccess={selectedAdmin.allClubsAccess}
                      togglingAllClubsAccess={togglingAllClubsAccess}
                      onToggleAllClubsAccess={handleToggleAllClubsAccess}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <UMAuthorityAddAdminModal
        show={showAddModal}
        mode={addMode}
        form={addForm}
        loading={addLoading}
        onModeChange={setAddMode}
        onFieldChange={handleAddFieldChange}
        onSubmit={handleAddSubmit}
        onClose={() => setShowAddModal(false)}
      />

      {showResetConfirmModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title">{t("staff:detail.permissions.resetConfirmTitle")}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowResetConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  {t("staff:detail.permissions.resetConfirmBody", { count: grants.length })}
                </p>
              </div>
              <div className="modal-footer">
                <AppButton variant="secondary" type="button" onClick={() => setShowResetConfirmModal(false)}>
                  {t("common:buttons.cancel")}
                </AppButton>
                <AppButton
                  variant="danger"
                  type="button"
                  disabled={resettingPermissions}
                  onClick={handleConfirmResetAllPermissions}
                >
                  {resettingPermissions && (
                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  )}
                  {t("staff:detail.permissions.resetConfirmButton")}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </UniversityManagerLayout>
  );
};

export default UMAuthorityManagement;
